// Replaces SubmitButton

import React, { ReactNode } from "react";
import AnimatedDots from "../nbc-ui/animated-dots";

// Define the props unique to our Button component
type ButtonOwnProps<E extends React.ElementType> = {
  as?: E;
  children: ReactNode;
};

// Combine our own props with the native props and refs of the chosen HTML element
type ButtonProps<E extends React.ElementType> = (
  ButtonOwnProps<E> & 
  Omit<React.ComponentPropsWithRef<E>, keyof ButtonOwnProps<E>> & {
    isProcessing?: boolean;
  }
);

export default function Button<E extends React.ElementType = "button">(props: ButtonProps<E>) {
  const { as, style, className, isProcessing, children, ...rest } = props;

  // Explicitly type the component using a generic function signature.
  // Otherwise, we'll get TypeScript error: 
  // `JSX element type 'Component' does not have any construct or call signatures.ts(2604)`  
  // because TypeScript cannot guarantee that the string or component passed to as is a valid JSX element.
  const Component: React.ElementType = as || "button";
  const isAnchor = Component === "a";

  // Automatically inject accessibility overrides matching the target tag
  const accessibilityProps = isAnchor
    ? { role: props.role ?? "button" }  // Treat an <a> anchor acting as a trigger as a button.
    : { type: props.type || "button" }; // Enforce type="button" to stop accidental form submission

  const processingIcon = <AnimatedDots top="12" color="white" width="35" height="15" 
    style={{ backgroundColor: "inherit", color: "#fff" }}
  />

  let finalClassName = "btn action-btn";

  if(isProcessing) {
    finalClassName += " btn-processing disabled";
  }

  if(className) {
    finalClassName += ` ${className}`;
  }

  if(as === "a") {
    finalClassName = finalClassName.replace(/btn/, "");
  }

  const appliedStyle = { borderRadius: "5px", cursor: "pointer", outline: 0, ...(style ?? {}) };

  return (
    <Component className={finalClassName} style={appliedStyle} {...accessibilityProps} {...rest}>
      { isProcessing ? processingIcon : children }
    </Component>
  );
}
